import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { ConfigProvider } from 'antd';
import { I18nextProvider } from 'react-i18next';
import dayjs from 'dayjs';
import 'dayjs/locale/ru';
import 'dayjs/locale/uz';
import 'dayjs/locale/uz-latn';
import ruRU from 'antd/es/locale/ru_RU';
import uzUZ from 'antd/es/locale/uz_UZ';
import enUS from 'antd/es/locale/en_US';

import i18n from './index';
import { getStoredLang, LANGS, persistLang } from './langs';

const LocaleContext = createContext({
    lang: 'uz-Cyrl',
    setLang: () => {},
    langs: LANGS,
});

const uzLatnAntd = {
    ...enUS,
    locale: 'uz-Latn',
    Pagination: {
        ...enUS.Pagination,
        items_per_page: '/ sahifa',
        jump_to: 'O‘tish',
        jump_to_confirm: 'tasdiqlash',
        page: '',
        prev_page: 'Oldingi sahifa',
        next_page: 'Keyingi sahifa',
    },
    DatePicker: {
        ...enUS.DatePicker,
        lang: {
            ...enUS.DatePicker?.lang,
            placeholder: 'Sanani tanlang',
            rangePlaceholder: ['Boshlanishi', 'Tugashi'],
            today: 'Bugun',
            now: 'Hozir',
            backToToday: 'Bugunga qaytish',
            ok: 'OK',
            clear: 'Tozalash',
            month: 'Oy',
            year: 'Yil',
            timeSelect: 'Vaqtni tanlang',
            dateSelect: 'Sanani tanlang',
            monthSelect: 'Oyni tanlang',
            yearSelect: 'Yilni tanlang',
        },
    },
    TimePicker: {
        ...enUS.TimePicker,
        placeholder: 'Vaqtni tanlang',
    },
    Empty: {
        ...enUS.Empty,
        description: 'Ma’lumot yo‘q',
    },
    Modal: {
        ...enUS.Modal,
        okText: 'OK',
        cancelText: 'Bekor qilish',
    },
};

const ANTD_LOCALES = {
    ru: ruRU,
    'uz-Cyrl': uzUZ,
    'uz-Latn': uzLatnAntd,
};

const DAYJS_LOCALES = {
    ru: 'ru',
    'uz-Cyrl': 'uz',
    'uz-Latn': 'uz-latn',
};

function applyDocumentLang(code) {
    const html = document.documentElement;
    html.lang = code === 'ru' ? 'ru' : code === 'uz-Latn' ? 'uz-Latn' : 'uz-Cyrl';
}

export function LocaleProvider({ children }) {
    const [lang, setLangState] = useState(getStoredLang);

    const setLang = useCallback((code) => {
        persistLang(code);
        i18n.changeLanguage(code);
        dayjs.locale(DAYJS_LOCALES[code] || 'uz');
        applyDocumentLang(code);
        setLangState(code);
    }, []);

    useEffect(() => {
        dayjs.locale(DAYJS_LOCALES[lang] || 'uz');
        applyDocumentLang(lang);
        if (i18n.language !== lang) {
            i18n.changeLanguage(lang);
        }
    }, [lang]);

    const value = useMemo(() => ({ lang, setLang, langs: LANGS }), [lang, setLang]);

    return (
        <LocaleContext.Provider value={value}>
            <I18nextProvider i18n={i18n}>
                <ConfigProvider
                    locale={ANTD_LOCALES[lang] || uzUZ}
                    theme={{
                        token: {
                            colorPrimary: '#1D2C7B',
                            colorLink: '#1D2C7B',
                            colorLinkHover: '#162563',
                            colorLinkActive: '#121d4d',
                        },
                    }}
                >
                    {children}
                </ConfigProvider>
            </I18nextProvider>
        </LocaleContext.Provider>
    );
}

export function useLocale() {
    return useContext(LocaleContext);
}
